import { ReactNode, useEffect, useState } from 'react'
import { BlurredBackDropSideSheet } from 'shared/components/BlurredBackDrop'
import { colors, media, zIndex, FC_SPRING_EASE } from 'shared/lib'
import styled, { css } from 'styled-components'

type Anchor = 'center' | 'right'
type MobileAnchor = 'center' | 'bottom'
type OverlayVariant = 'blurred-light' | 'dark'

type ModalProps = {
  children?: ReactNode
  hasPadding?: boolean
  isOpen: boolean
  noBorder?: boolean
  widthOverride?: string
  noMinHeight?: boolean
  anchor?: Anchor
  mobileAnchor?: MobileAnchor
  mobileFullScreen?: boolean
  overlayVariant?: OverlayVariant
  animated?: boolean
  onClose?: () => void
}

const EXIT_ANIMATION_MS = 350

export const SideSheetModal = ({
  children,
  hasPadding = true,
  isOpen,
  noBorder,
  widthOverride,
  noMinHeight,
  anchor = 'center',
  mobileAnchor = 'center',
  mobileFullScreen = false,
  overlayVariant = 'blurred-light',
  animated = false,
  onClose,
}: ModalProps) => {
  const [isMounted, setIsMounted] = useState(isOpen)
  const [isEntering, setIsEntering] = useState(false)

  useEffect(() => {
    if (!animated) {
      setIsMounted(isOpen)
      return undefined
    }
    if (isOpen) {
      setIsMounted(true)
      const raf = requestAnimationFrame(() => setIsEntering(true))
      return () => cancelAnimationFrame(raf)
    }
    setIsEntering(false)
    const timeout = window.setTimeout(() => setIsMounted(false), EXIT_ANIMATION_MS)
    return () => window.clearTimeout(timeout)
  }, [animated, isOpen])

  useEffect(() => {
    if (!animated || !isOpen || !onClose) return undefined
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [animated, isOpen, onClose])

  useEffect(() => {
    if (!animated) return undefined
    if (isMounted) {
      const prev = document.documentElement.style.overflow
      document.documentElement.style.overflow = 'hidden'
      return () => {
        document.documentElement.style.overflow = prev
      }
    }
    return undefined
  }, [animated, isMounted])

  if (!animated) {
    return isOpen ? (
      <ModalBlurredBackDrop data-dismiss-on-click isActive={isOpen}>
        <Content
          widthOverride={widthOverride}
          hasPadding={hasPadding}
          noBorder={noBorder}
          noMinHeight={noMinHeight}
        >
          {children}
        </Content>
      </ModalBlurredBackDrop>
    ) : null
  }

  if (!isMounted) return null

  return (
    <>
      <AnimatedOverlay
        data-qa="side-sheet-overlay"
        variant={overlayVariant}
        entered={isEntering}
        onClick={onClose}
      />
      <AnimatedPanel
        anchor={anchor}
        mobileAnchor={mobileAnchor}
        mobileFullScreen={mobileFullScreen}
        entered={isEntering}
        widthOverride={widthOverride}
        hasPadding={hasPadding}
      >
        {children}
      </AnimatedPanel>
    </>
  )
}

export default SideSheetModal

const ModalBlurredBackDrop = styled(BlurredBackDropSideSheet)`
  ${media.large`
    z-index: ${zIndex.justOverNav};
  `}
`

const Content = styled.div<{
  widthOverride?: string
  hasPadding?: boolean
  noBorder?: boolean
  noMinHeight?: boolean
}>`
  ${({ noBorder }) => (noBorder ? '' : `border: 1px solid ${colors.FC2_BLACK};`)}
  ${({ noMinHeight }) => (noMinHeight ? '' : 'min-height: 20%;')}
  background-color: ${colors.FC2_WHITE};
  margin: 20px;
  padding: ${({ hasPadding }) => (hasPadding ? '20px' : 0)};
  width: 100%;
  z-index: ${zIndex.sideSheetModal};

  ${({ widthOverride }) =>
    media.large`
      width: ${widthOverride ? widthOverride : '445px'};
    `}
`

const AnimatedOverlay = styled.div<{ variant: OverlayVariant; entered: boolean }>`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.pageTakeover};
  opacity: ${({ entered }) => (entered ? 1 : 0)};
  transition: opacity 300ms ease;
  ${({ variant }) =>
    variant === 'dark'
      ? css`
          background: rgb(0 0 0 / 45%);
        `
      : css`
          background: rgb(255 255 255 / 80%);
          backdrop-filter: blur(4px);
        `}
`

const AnimatedPanel = styled.div<{
  anchor: Anchor
  mobileAnchor: MobileAnchor
  mobileFullScreen: boolean
  entered: boolean
  widthOverride?: string
  hasPadding?: boolean
}>`
  position: fixed;
  background-color: ${colors.FC2_WHITE};
  z-index: ${zIndex.sideSheetModal};
  transition: transform ${EXIT_ANIMATION_MS}ms ${FC_SPRING_EASE};
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${({ mobileAnchor, mobileFullScreen, entered }) =>
    mobileAnchor === 'bottom'
      ? css`
          left: 0;
          right: 0;
          bottom: 0;
          ${mobileFullScreen
            ? css`
                top: 0;
                height: 100dvh;
                max-height: 100dvh;
                border-radius: 0;
              `
            : css`
                max-height: 90dvh;
                border-radius: 8px 8px 0 0;
              `}
          transform: translateY(${entered ? '0' : '100%'});
        `
      : css`
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%) scale(${entered ? 1 : 0.98});
        `}

  ${({ anchor, entered, widthOverride }) =>
    anchor === 'right' &&
    media.larger`
      top: 0;
      right: 0;
      bottom: 0;
      left: auto;
      width: ${widthOverride || '480px'};
      max-height: 100dvh;
      border-radius: 0;
      transform: translateX(${entered ? '0' : '100%'});
    `}

  ${({ hasPadding }) => (hasPadding ? 'padding: 0;' : '')}
`
